import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import {
  attendanceStatusLabel,
  attendanceStatusTone,
} from "../../../utils/attendanceDisplay";
import AcceptMembershipPayment from "../common/AcceptMembershipPayment";
import VoidManualPaymentButton from "../common/VoidManualPaymentButton";
import { HubSheet } from "../AdminPortal/HubPwa";
import { absenceReasonLabel } from "../AdminSchedule/calendarUtils";
import {
  formatDateShort,
  isActiveMembershipPayment,
  membershipEffectivePrice,
  membershipStatusLabel,
  membershipStatusTone,
  paymentMethodLabel,
  pendingMembershipLabel,
} from "./clientSportDisplay";
import {
  analyzeMembershipAssignBlock,
  isMembershipCurrentlyActive,
  isMembershipFrozenNow,
  listCurrentMemberships,
} from "../../../utils/membershipAssignUtils";
import MembershipAdjustPanel from "../common/MembershipAdjustPanel";
import { membershipScopeBadge } from "../common/MembershipGroupScopeField";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function fmtMoney(n) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function monthRange(year, month) {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const m = String(month + 1).padStart(2, "0");
    const day = String(d).padStart(2, "0");
    cells.push(`${year}-${m}-${day}`);
  }
  return cells;
}

function dateKey(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

function dayTonePriority(tone) {
  const order = { present: 4, excused: 3, absent: 2, expected: 1, scheduled: 0.5 };
  return order[tone] || 0;
}

function miniCalClass(tone) {
  if (tone === "present") return s.attendanceMiniPresent;
  if (tone === "absent") return s.attendanceMiniAbsent;
  if (tone === "excused") return s.attendanceMiniExcused;
  if (tone === "expected") return s.attendanceMiniExpected;
  if (tone === "scheduled") return s.attendanceMiniScheduled;
  return "";
}

function membershipTone(status, notes) {
  const tone = membershipStatusTone(status, notes);
  if (tone === "ok" || tone === "success") return "active";
  if (tone === "warn") return status === "pending" ? "pending" : "warn";
  if (status === "expired") return "expired";
  return "muted";
}

function membershipFreezeLimit(membership) {
  return Math.max(0, Number(membership?.plan?.freeze_days_allowed) || 0);
}

export function PatientMembershipPanel({ patientId, payments, onChanged, compact = false }) {
  const toast = useToast();
  const [memberships, setMemberships] = useState([]);
  const [plans, setPlans] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [renewalMode, setRenewalMode] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [assignForm, setAssignForm] = useState({
    plan_id: "",
    starts_at: new Date().toISOString().slice(0, 10),
    visits_remaining: "",
    price: "",
  });
  const [assignBusy, setAssignBusy] = useState(false);
  const [payFormOpen, setPayFormOpen] = useState(false);
  const [freezeSheet, setFreezeSheet] = useState({
    open: false,
    session: 0,
    membershipId: "",
    membershipName: "",
    allowedDays: 0,
    days: "7",
    busy: false,
  });
  const [adjustOpenId, setAdjustOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, p, g] = await Promise.all([
        clinicAPI.getClientMemberships({ patient_id: patientId }),
        // Админ выдаёт любой активный тариф — без фильтра по группам клиента
        clinicAPI.getMembershipPlans(),
        clinicAPI.getSportGroupsSummary(),
      ]);
      setMemberships(m.memberships || []);
      setPlans(p.plans || []);
      setGroups((g.groups || []).filter((gr) => gr.is_active !== false));
    } catch {
      setMemberships([]);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const assignablePlans = useMemo(
    () => plans.filter((p) => p?.is_active !== false),
    [plans],
  );

  useEffect(() => {
    if (
      assignForm.plan_id &&
      !assignablePlans.some((p) => String(p.id) === String(assignForm.plan_id))
    ) {
      setAssignForm((f) => ({ ...f, plan_id: "", visits_remaining: "" }));
    }
  }, [assignablePlans, assignForm.plan_id]);

  const activeMemberships = listCurrentMemberships(memberships, patientId);
  const currentMembership = activeMemberships[0] || null;
  const pending = memberships.find((m) => m.status === "pending");
  const openDebtMemberships = memberships.filter(
    (m) =>
      Number(m.amount_due) > 0 &&
      (m.status === "pending" ||
        m.status === "active" ||
        m.status === "frozen"),
  );
  const openDebtTotal = openDebtMemberships.reduce(
    (sum, m) => sum + Number(m.amount_due || 0),
    0,
  );
  const assignPlan = assignablePlans.find(
    (p) => String(p.id) === String(assignForm.plan_id),
  ) || plans.find((p) => String(p.id) === String(assignForm.plan_id));
  const assignBlock = analyzeMembershipAssignBlock(
    memberships,
    patientId,
    assignPlan,
  );

  const openAssign = () => {
    setRenewalMode(false);
    setShowAssign(true);
  };

  const openRenewal = () => {
    if (!assignBlock.renewalStartsAt) return;
    setAssignForm((f) => ({
      ...f,
      starts_at: assignBlock.renewalStartsAt,
    }));
    setRenewalMode(true);
    setShowAssign(true);
  };

  const openFreezeSheet = (membership) => {
    const allowedDays = membershipFreezeLimit(membership);
    if (allowedDays < 1) {
      toast.error("Для этого тарифа заморозка не предусмотрена");
      return;
    }
    setFreezeSheet((state) => ({
      open: true,
      session: (state.session || 0) + 1,
      membershipId: membership?.id || "",
      membershipName: membership?.plan?.name || "Абонемент",
      allowedDays,
      days: String(Math.max(1, Math.min(7, allowedDays))),
      busy: false,
    }));
  };

  const resetFreezeSheet = () => {
    setFreezeSheet((state) => ({
      open: false,
      session: state.session || 0,
      membershipId: "",
      membershipName: "",
      allowedDays: 0,
      days: "7",
      busy: false,
    }));
  };

  const closeFreezeSheet = () => {
    setFreezeSheet((state) =>
      state.busy
        ? state
        : {
            open: false,
            session: state.session || 0,
            membershipId: "",
            membershipName: "",
            allowedDays: 0,
            days: "7",
            busy: false,
          },
    );
  };

  const freeze = async () => {
    const days = Number(String(freezeSheet.days).trim());
    if (freezeSheet.allowedDays < 1) {
      toast.error("Для этого тарифа заморозка не предусмотрена");
      return;
    }
    if (!Number.isInteger(days) || days < 1) {
      toast.error("Укажите срок заморозки в днях");
      return;
    }
    if (days > freezeSheet.allowedDays) {
      toast.error(`Можно заморозить не больше чем на ${freezeSheet.allowedDays} дн.`);
      return;
    }
    try {
      setFreezeSheet((state) => ({ ...state, busy: true }));
      await clinicAPI.freezeMembership(freezeSheet.membershipId, days);
      toast.success(`Заморозка на ${days} дн.`);
      resetFreezeSheet();
      load();
      onChanged?.();
    } catch (e) {
      setFreezeSheet((state) => ({ ...state, busy: false }));
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const unfreeze = async (id) => {
    try {
      await clinicAPI.unfreezeMembership(id);
      toast.success("Заморозка снята");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const assignPlanResolved = assignablePlans.find(
    (p) => String(p.id) === String(assignForm.plan_id),
  ) || plans.find((p) => String(p.id) === String(assignForm.plan_id));
  const assignIsVisits = assignPlanResolved?.plan_type === "visits";
  const groupNameById = Object.fromEntries(groups.map((g) => [g.id, g.name]));

  const assign = async (e) => {
    e.preventDefault();
    if (!assignForm.plan_id) {
      toast.error("Выберите тариф");
      return;
    }
    if (!renewalMode && assignBlock.blocked) {
      toast.error(assignBlock.message || "Нельзя выдать абонемент");
      return;
    }
    if (assignIsVisits) {
      const n = Number(assignForm.visits_remaining);
      if (
        assignForm.visits_remaining === "" ||
        !Number.isFinite(n) ||
        !Number.isInteger(n) ||
        n < 0
      ) {
        toast.error("Остаток занятий: целое число от 0");
        return;
      }
    }
    if (assignPlanResolved?.price_on_issue) {
      const priceN = Number(String(assignForm.price).replace(",", "."));
      if (!Number.isFinite(priceN) || priceN <= 0) {
        toast.error("Укажите сумму абонемента");
        return;
      }
    }
    setAssignBusy(true);
    try {
      const payload = {
        patient_id: patientId,
        plan_id: assignForm.plan_id,
        starts_at: assignForm.starts_at,
        renewal: renewalMode,
      };
      if (assignIsVisits) {
        payload.visits_remaining = Number(assignForm.visits_remaining);
      }
      if (assignPlanResolved?.price_on_issue) {
        payload.price = Number(String(assignForm.price).replace(",", "."));
      }
      const data = await clinicAPI.createClientMembership(payload);
      const activated = data.membership?.status === "active";
      const due =
        data.membership?.amount_due != null
          ? Number(data.membership.amount_due)
          : membershipEffectivePrice(data.membership || assignPlanResolved);
      toast.success(
        activated && due > 0
          ? renewalMode
            ? "Продление активно — долг в Финансы → Должники"
            : "Абонемент активен — долг в Финансы → Должники"
          : activated
            ? "Абонемент выдан и активирован"
            : due <= 0
              ? "Абонемент выдан — нажмите «Активировать»"
              : renewalMode
                ? "Следующий абонемент оформлен — запишите оплату"
                : "Абонемент выдан — запишите оплату",
      );
      setAssignForm((f) => ({
        ...f,
        plan_id: "",
        visits_remaining: "",
        price: "",
      }));
      setRenewalMode(false);
      setShowAssign(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setAssignBusy(false);
    }
  };

  return (
    <div className={s.clientProfileTabPanel}>
      {openDebtTotal > 0 ? (
        <p className={s.clientTabMuted} style={{ marginBottom: 8 }}>
          Есть долг {fmtMoney(openDebtTotal)}
          {openDebtMemberships.length > 1
            ? ` по ${openDebtMemberships.length} абонементам`
            : ""}
          — смотрите блок ниже или Финансы → Должники.
        </p>
      ) : null}
      {pending ? (
        <section className={s.clientTabCard}>
          {membershipEffectivePrice(pending) <= 0 ? (
            <>
              <h3 className={s.clientTabCardTitle}>
                {pendingMembershipLabel(pending)}
              </h3>
              {!payFormOpen ? (
                <p className={s.clientTabMuted}>
                  «{pending.plan?.name || "Абонемент"}» — бесплатный тариф.
                </p>
              ) : null}
            </>
          ) : (
            <>
              <h3 className={s.clientTabCardTitle}>
                {pendingMembershipLabel(pending)}
              </h3>
              {!payFormOpen ? (
                <p className={s.clientTabMuted}>
                  «{pending.plan?.name || "Абонемент"}» —{" "}
                  {fmtMoney(membershipEffectivePrice(pending))}
                </p>
              ) : (
                <p className={s.clientTabMuted}>Укажите сумму и способ оплаты</p>
              )}
            </>
          )}
          <div className={s.membershipPendingActions}>
            <AcceptMembershipPayment
              membership={pending}
              patientId={patientId}
              onOpenChange={setPayFormOpen}
              onSuccess={() => {
                setPayFormOpen(false);
                load();
                onChanged?.();
              }}
            />
          </div>
        </section>
      ) : null}

      {activeMemberships.length > 0 ? (
        activeMemberships.map((m) => {
          const freezeLimit = membershipFreezeLimit(m);
          const frozenNow = isMembershipFrozenNow(m);
          return (
          <section key={m.id} className={s.clientTabCard}>
            <div className={s.membershipActiveHead}>
              <h3 className={s.clientTabCardTitle}>
                {frozenNow ? "Замороженный абонемент" : "Активный абонемент"}
              </h3>
            </div>
            <dl className={s.membershipActiveFacts}>
              <div>
                <dt>Тариф</dt>
                <dd>{m.plan?.name || "—"}</dd>
              </div>
              {m.visits_remaining != null ? (
                <div>
                  <dt>Остаток</dt>
                  <dd>{m.visits_remaining} зан.</dd>
                </div>
              ) : null}
              <div>
                <dt>Группы</dt>
                <dd>{membershipScopeBadge(m, groupNameById)}</dd>
              </div>
              <div>
                <dt>Срок</dt>
                <dd>
                  {frozenNow && m.frozen_until
                    ? `заморожен до ${formatDateShort(m.frozen_until)}`
                    : m.ends_at
                      ? `до ${formatDateShort(m.ends_at)}`
                      : "без ограничения"}
                </dd>
              </div>
              {Number(m.amount_due) > 0 ? (
                <div>
                  <dt>Долг</dt>
                  <dd>{fmtMoney(m.amount_due)}</dd>
                </div>
              ) : Number(m.paid_total) > 0 ? (
                <div>
                  <dt>Оплачено</dt>
                  <dd>{fmtMoney(m.paid_total)}</dd>
                </div>
              ) : membershipEffectivePrice(m) > 0 ? (
                <div>
                  <dt>Сумма</dt>
                  <dd>{fmtMoney(membershipEffectivePrice(m))}</dd>
                </div>
              ) : null}
            </dl>
            <div className={s.membershipActiveActions}>
              <AcceptMembershipPayment
                membership={m}
                patientId={patientId}
                onSuccess={() => {
                  load();
                  onChanged?.();
                }}
                extraActions={[
                  frozenNow
                    ? {
                        id: "unfreeze",
                        label: "Разморозить",
                        onClick: () => unfreeze(m.id),
                      }
                    : freezeLimit > 0
                      ? {
                          id: "freeze",
                          label: "Заморозить",
                          onClick: () => openFreezeSheet(m),
                        }
                      : null,
                  {
                    id: "adjust",
                    label: "Изменить занятия и срок",
                    onClick: () => setAdjustOpenId(m.id),
                  },
                ].filter(Boolean)}
              />
              <MembershipAdjustPanel
                membership={m}
                hideToggle
                initialOpen={adjustOpenId === m.id}
                onRequestClose={() => setAdjustOpenId(null)}
                onChanged={() => {
                  load();
                  onChanged?.();
                }}
              />
            </div>
            {freezeLimit > 0 && !frozenNow ? (
              <p className={s.clientTabMuted} style={{ marginTop: 8 }}>
                Заморозка — если ученик болеет или уезжает: срок продлевается
                (до {freezeLimit} дн. по тарифу).
              </p>
            ) : null}
          </section>
          );
        })
      ) : (
        <section className={s.clientTabCard}>
          <p className={s.clientTabMuted}>Нет активного абонемента</p>
        </section>
      )}

      {compact && !showAssign && !pending ? (
        <div className={s.clientTabFormRow} style={{ flexWrap: "wrap", gap: 8 }}>
          {!assignBlock.blocked ? (
            <button
              type="button"
              className={s.clientProfileInlineLink}
              onClick={openAssign}
            >
              + Выдать абонемент
            </button>
          ) : null}
          {assignBlock.canRenew ? (
            <button
              type="button"
              className={s.clientProfileInlineLink}
              onClick={openRenewal}
            >
              {assignBlock.needsEndDate
                ? "Выдать следующий абонемент"
                : `Следующий с ${formatDateShort(assignBlock.renewalStartsAt)}`}
            </button>
          ) : null}
          {assignBlock.blocked && !assignBlock.canRenew ? (
            <p className={s.clientTabMuted}>{assignBlock.message}</p>
          ) : null}
        </div>
      ) : null}

      {(!compact || showAssign) && !pending ? (
        <section className={s.clientTabCard}>
          {compact ? (
            <div className={s.clientProfileEmbeddedHead}>
              <h3 className={s.clientTabCardTitle}>
                {renewalMode
                  ? "Следующий абонемент"
                  : "Выдать абонемент"}
              </h3>
              <button
                type="button"
                className={s.clientProfileSectionAction}
                onClick={() => {
                  setShowAssign(false);
                  setRenewalMode(false);
                }}
              >
                Скрыть
              </button>
            </div>
          ) : (
            <h3 className={s.clientTabCardTitle}>
              {renewalMode ? "Следующий абонемент" : "Выдать абонемент"}
            </h3>
          )}
          {renewalMode ? null : assignBlock.blocked ? (
            <p className={s.clientTabMuted}>{assignBlock.message}</p>
          ) : null}
          {(renewalMode || !assignBlock.blocked) ? (
          <>
          <form
            onSubmit={assign}
            className={s.membershipIssueForm}
          >
            <Field label="Тариф" className={s.membershipIssuePlanField}>
              <Select
                value={assignForm.plan_id}
                onChange={(e) => {
                  const planId = e.target.value;
                  const plan = plans.find((p) => String(p.id) === String(planId));
                  setAssignForm({
                    ...assignForm,
                    plan_id: planId,
                    visits_remaining:
                      plan?.plan_type === "visits" && plan.visits_count != null
                        ? String(plan.visits_count)
                        : "",
                  });
                }}
              >
                <option value="">
                  {assignablePlans.length ? "Выберите…" : "Нет активных тарифов"}
                </option>
                {assignablePlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.price_on_issue
                      ? " · цена при выдаче"
                      : p.price != null
                        ? ` · ${fmtMoney(p.price)}`
                        : ""}
                    {p.plan_type === "visits" && p.visits_count != null
                      ? ` · ${p.visits_count} зан.`
                      : ""}
                    {p.plan_type === "time" && p.duration_days
                      ? ` · ${p.duration_days} дн.`
                      : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="С даты" className={s.membershipIssueDateField}>
              <Input
                type="date"
                value={assignForm.starts_at}
                min={renewalMode ? assignBlock.renewalStartsAt || undefined : undefined}
                onChange={(e) =>
                  setAssignForm({ ...assignForm, starts_at: e.target.value })
                }
              />
            </Field>
            {assignIsVisits ? (
              <Field
                label="Занятий"
                className={s.membershipIssueVisitsField}
              >
                <Input
                  type="number"
                  min="0"
                  max={Number(assignPlan?.visits_count) || undefined}
                  step="1"
                  value={assignForm.visits_remaining}
                  onChange={(e) =>
                    setAssignForm({
                      ...assignForm,
                      visits_remaining: e.target.value,
                    })
                  }
                />
              </Field>
            ) : null}
            {assignPlanResolved?.price_on_issue ? (
              <Field label="Сумма, ₽" className={s.membershipIssueVisitsField}>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={assignForm.price}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, price: e.target.value })
                  }
                  placeholder="Договорная цена"
                  required
                />
              </Field>
            ) : null}
            <Field label={"\u00a0"} className={s.membershipIssueActionsField}>
              <div className={s.membershipIssueActions}>
                <Button
                  type="submit"
                  disabled={assignBusy || !assignForm.plan_id}
                >
                  {renewalMode ? "Оформить" : "Выдать"}
                </Button>
                {renewalMode || showAssign ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={assignBusy}
                    onClick={() => {
                      setShowAssign(false);
                      setRenewalMode(false);
                    }}
                  >
                    Отмена
                  </Button>
                ) : null}
              </div>
            </Field>
          </form>
          </>
          ) : assignBlock.canRenew && !renewalMode ? (
            <Button type="button" variant="secondary" onClick={openRenewal}>
              Выдать следующий абонемент
            </Button>
          ) : null}
        </section>
      ) : null}

      {memberships.length > 0 && (!compact || showHistory) ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Все абонементы</h3>
          {compact ? (
            <ul className={s.clientGroupList}>
              {memberships.map((m) => {
                const frozenNow = isMembershipFrozenNow(m);
                return (
                  <li key={m.id}>
                    <div className={s.clientGroupListItem}>
                      <strong>{m.plan?.name || "—"}</strong>
                      <span className={s.clientTabMuted}>
                        {frozenNow
                          ? `Заморожен до ${formatDateShort(m.frozen_until)}`
                          : m.starts_at
                            ? formatDateShort(m.starts_at)
                            : "—"}
                        {m.ends_at && !frozenNow
                          ? ` — ${formatDateShort(m.ends_at)}`
                          : ""}
                        {" · "}
                        {m.visits_remaining != null
                          ? `${m.visits_remaining} зан.`
                          : "без лимита"}
                        {" · "}
                        {membershipStatusLabel(m, m.notes)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={s.membershipHistoryWrap}>
              <table className={s.membershipHistoryTable}>
                <thead>
                  <tr>
                    <th>Тариф</th>
                    <th>Период</th>
                    <th>Остаток</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((m) => (
                    <tr
                      key={m.id}
                      className={s[`clientDataTableRow_${membershipTone(m, m.notes)}`]}
                    >
                      <td>{m.plan?.name || "—"}</td>
                      <td>
                        {m.starts_at ? formatDateShort(m.starts_at) : "—"}
                        {m.ends_at ? ` — ${formatDateShort(m.ends_at)}` : ""}
                      </td>
                      <td>
                        {m.visits_remaining != null
                          ? `${m.visits_remaining} зан.`
                          : "—"}
                      </td>
                      <td>{membershipStatusLabel(m, m.notes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {compact && !showHistory && (memberships.length > 0 || payments?.length > 0) ? (
        <button
          type="button"
          className={s.clientProfileInlineLink}
          onClick={() => setShowHistory(true)}
        >
          Показать историю абонементов и платежей
        </button>
      ) : null}

      {payments?.filter(isActiveMembershipPayment).length > 0 &&
      (!compact || showHistory) ? (
        <section className={s.clientTabCard}>
          <h3 className={s.clientTabCardTitle}>Платежи</h3>
          <ul className={s.clientSimpleList}>
            {payments.filter(isActiveMembershipPayment).map((p) => (
              <li key={p.id} className={s.clientPaymentRow}>
                <span>
                  <strong>{fmtMoney(p.amount)}</strong>
                  <span className={s.clientTabMuted}>
                    {" "}
                    · {formatDateShort(p.paid_at)} · {paymentMethodLabel(p.method)}
                  </span>
                </span>
                <VoidManualPaymentButton payment={p} onSuccess={onChanged} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {freezeSheet.open ? (
        <HubSheet
          key={`freeze-${freezeSheet.session}`}
          open
          onClose={closeFreezeSheet}
          title="Заморозить абонемент"
          subtitle={freezeSheet.membershipName}
        >
          <div className={s.clientTabFormStack}>
            <p className={s.clientTabMuted}>
              Укажите, на сколько дней ученик не будет ходить. Срок действия
              абонемента продлится на это же число дней.
            </p>
            <p className={s.clientTabMuted}>
              По тарифу доступно до {freezeSheet.allowedDays || 0} дн. заморозки.
            </p>
            <Field label="Дней заморозки">
              <Input
                type="number"
                min="1"
                max={freezeSheet.allowedDays || undefined}
                step="1"
                value={freezeSheet.days}
                onChange={(e) =>
                  setFreezeSheet((state) => ({ ...state, days: e.target.value }))
                }
              />
            </Field>
            <div className={s.clientTabFormRow} style={{ gap: 8 }}>
              <Button type="button" variant="secondary" onClick={closeFreezeSheet}>
                Отмена
              </Button>
              <Button type="button" onClick={freeze} disabled={freezeSheet.busy}>
                {freezeSheet.busy ? "Сохраняем…" : "Заморозить"}
              </Button>
            </div>
          </div>
        </HubSheet>
      ) : null}
    </div>
  );
}

export function PatientAttendancePanel({ patientId }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { from, to } = monthRange(viewYear, viewMonth);
    try {
      const r = await clinicAPI.getPatientSportAttendance(patientId, { from, to });
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, viewYear, viewMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.attendances || [];
  const stats = data?.stats;

  const dayMap = useMemo(() => {
    const map = {};
    for (const a of rows) {
      const key = dateKey(a.session?.start_datetime);
      if (!key) continue;
      const tone = attendanceStatusTone(a);
      if (!map[key] || dayTonePriority(tone) > dayTonePriority(map[key])) {
        map[key] = tone;
      }
    }
    return map;
  }, [rows]);

  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const monthTitle = new Date(viewYear, viewMonth, 1).toLocaleDateString(
    "ru-RU",
    { month: "long", year: "numeric" },
  );

  const goMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setSelectedDay(null);
  };

  const sorted = useMemo(() => {
    let list = [...rows];
    if (selectedDay) {
      list = list.filter((a) => dateKey(a.session?.start_datetime) === selectedDay);
    }
    return list.sort((a, b) => {
      const ta = new Date(a.session?.start_datetime || 0).getTime();
      const tb = new Date(b.session?.start_datetime || 0).getTime();
      return ta - tb;
    });
  }, [rows, selectedDay]);

  const monthPct =
    stats && stats.present + stats.absent > 0
      ? Math.round((stats.present / (stats.present + stats.absent)) * 100)
      : null;

  const logToneClass = (tone) => {
    if (tone === "present") return s.clientAttendanceLogPresent;
    if (tone === "absent") return s.clientAttendanceLogAbsent;
    if (tone === "excused") return s.clientAttendanceLogExcused;
    if (tone === "scheduled") return s.clientAttendanceLogScheduled;
    return s.clientAttendanceLogExpected;
  };

  const membershipHint =
    data?.membership?.plan_type === "visits" &&
    data.membership.visits_remaining != null
      ? `${data.membership.visits_remaining} зан. по абонементу`
      : data?.membership?.ends_at
        ? `до ${formatDateShort(data.membership.ends_at)}`
        : null;

  return (
    <div className={s.clientProfileTabPanel}>
      {stats ? (
        <div className={s.clientAttendanceSummary}>
          <div className={s.clientAttendanceSummaryItem}>
            <span className={s.clientAttendanceSummaryVal}>{stats.present}</span>
            <span className={s.clientAttendanceSummaryLbl}>был</span>
          </div>
          <div className={s.clientAttendanceSummaryItem}>
            <span className={s.clientAttendanceSummaryVal}>{stats.absent}</span>
            <span className={s.clientAttendanceSummaryLbl}>пропуск</span>
          </div>
          {(data?.membership || (stats.expected ?? 0) > 0) ? (
            <div className={s.clientAttendanceSummaryItem}>
              <span className={s.clientAttendanceSummaryVal}>
                {stats.expected ?? 0}
              </span>
              <span className={s.clientAttendanceSummaryLbl}>
                ожидается
                {membershipHint ? (
                  <small className={s.clientAttendanceSummarySub}>
                    {membershipHint}
                  </small>
                ) : null}
              </span>
            </div>
          ) : (
            <div className={s.clientAttendanceSummaryItem}>
              <span className={s.clientAttendanceSummaryVal}>
                {monthPct != null ? `${monthPct}%` : "—"}
              </span>
              <span className={s.clientAttendanceSummaryLbl}>за месяц</span>
            </div>
          )}
        </div>
      ) : null}

      <section className={s.clientTabCard}>
        <div className={s.clientAttendanceCalHead}>
          <button
            type="button"
            className={s.clientAttendanceCalNav}
            onClick={() => goMonth(-1)}
            aria-label="Предыдущий месяц"
          >
            ‹
          </button>
          <h3 className={s.clientTabCardTitle}>{monthTitle}</h3>
          <button
            type="button"
            className={s.clientAttendanceCalNav}
            onClick={() => goMonth(1)}
            aria-label="Следующий месяц"
          >
            ›
          </button>
        </div>

        <div className={s.attendanceMiniWeekdays}>
          {WEEKDAYS.map((w) => (
            <span key={w} className={s.attendanceMiniWeekday}>
              {w}
            </span>
          ))}
        </div>

        <div className={s.attendanceMiniCalWide}>
          {cells.map((dateStr, idx) => {
            if (!dateStr) {
              return (
                <span
                  key={`empty-${idx}`}
                  className={`${s.attendanceMiniDay} ${s.attendanceMiniDay_empty}`}
                  aria-hidden
                />
              );
            }
            const tone = dayMap[dateStr];
            const isSelected = selectedDay === dateStr;
            const dayNum = Number(dateStr.slice(8, 10));
            return (
              <button
                key={dateStr}
                type="button"
                className={`${s.attendanceMiniDay} ${miniCalClass(tone)} ${
                  isSelected ? s.attendanceMiniDay_selected : ""
                }`}
                onClick={() =>
                  setSelectedDay((d) => (d === dateStr ? null : dateStr))
                }
                title={
                  tone
                    ? {
                        present: "Был",
                        absent: "Не был",
                        excused: "Предупредил",
                        expected: "Ожидается по абонементу",
                        scheduled: "Тренировка вне лимита абонемента",
                      }[tone]
                    : "Нет занятия"
                }
              >
                {dayNum}
              </button>
            );
          })}
        </div>

        <div className={s.attendanceMiniLegend}>
          <span>
            <i className={s.attendanceMiniPresent} /> был
          </span>
          <span>
            <i className={s.attendanceMiniAbsent} /> не был
          </span>
          <span>
            <i className={s.attendanceMiniExcused} /> предупредил
          </span>
          <span>
            <i className={s.attendanceMiniExpected} /> ожидается
          </span>
          <span>
            <i className={s.attendanceMiniScheduled} /> вне абонемента
          </span>
        </div>
        <p className={s.clientAttendanceHint}>
          Нажмите на день, чтобы отфильтровать список. «Ожидается» — ближайшие
          занятия в пределах абонемента (не больше остатка занятий). Серые дни —
          тренировки сверх лимита или без абонемента.
        </p>
      </section>

      <section className={s.clientTabCard}>
        <div className={s.clientAttendanceLogHead}>
          <h3 className={s.clientTabCardTitle}>
            {selectedDay
              ? `Занятия ${formatDateRu(`${selectedDay}T12:00:00`)}`
              : "Все занятия за месяц"}
          </h3>
          {selectedDay ? (
            <button
              type="button"
              className={s.clientAttendanceClearFilter}
              onClick={() => setSelectedDay(null)}
            >
              Показать все
            </button>
          ) : null}
        </div>

        {loading ? (
          <p className={s.clientTabMuted}>Загрузка…</p>
        ) : sorted.length === 0 ? (
          <p className={s.clientTabMuted}>Нет занятий за выбранный период</p>
        ) : (
          <ul className={s.clientAttendanceLog}>
            {sorted.map((a) => {
              const tone = attendanceStatusTone(a);
              const reason = absenceReasonLabel(a.absence_reason);
              const showReason =
                reason &&
                (tone === "absent" || tone === "excused");
              return (
                <li key={a.id} className={s.clientAttendanceLogItem}>
                  <div>
                    <strong>
                      {formatDateRu(a.session?.start_datetime)}{" "}
                      {formatTimeRu(a.session?.start_datetime)}
                    </strong>
                    <span className={s.clientTabMuted}>
                      {" "}
                      · {a.session?.group?.name || "—"}
                    </span>
                    {showReason ? (
                      <div className={s.clientTabMuted}>Причина: {reason}</div>
                    ) : null}
                  </div>
                  <span className={logToneClass(tone)}>
                    {attendanceStatusLabel(a)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
